export const SPACES_ENDPOINT = "https://strangers-business-profile.ams3.digitaloceanspaces.com";
export const SPACES_BUCKET = "strangers-business-profile";
export const ACTIVITIES_ROOT_PREFIX = "strangers/activities/";

const SPACES_HOST = "strangers-business-profile.ams3.digitaloceanspaces.com";

const stripQueryAndHash = (input) => input.split(/[?#]/, 1)[0];

const readString = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
};

const readActivityRawId = (activity) => {
  const candidates = [
    activity?.id_event,
    activity?.idEvent,
    activity?.event_id,
    activity?.activity_id,
    activity?.id_event_raw,
  ];
  for (const candidate of candidates) {
    const id = readString(candidate);
    if (id) {
      return id;
    }
  }
  return "";
};

const readActivityPathCandidates = (activity) => {
  const candidates = [
    activity?.key,
    activity?.s3_key,
    activity?.json_key,
    activity?.activity_key,
    activity?.path,
    activity?.source_path,
    activity?.url,
    activity?.source_url,
    activity?.source,
    activity?.file,
    activity?.file_name,
    activity?.filename,
  ];
  return candidates.map(readString).filter(Boolean);
};

const parseImagesInput = (images) => {
  if (Array.isArray(images)) {
    return images;
  }

  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.url === "string") {
          return [parsed];
        }
        return Object.values(parsed);
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  if (images && typeof images === "object") {
    if (typeof images.url === "string") {
      return [images];
    }
    return Object.values(images);
  }

  return [];
};

const parseJsonFileName = (fileName, hintedIdEvent = "") => {
  const normalized = readString(fileName);
  const fileStem = normalized.replace(/\.json$/i, "");
  const hinted = readString(hintedIdEvent);

  if (hinted && fileStem.startsWith(`${hinted}_`)) {
    const legacyPart = fileStem.slice(hinted.length + 1);
    return {
      fileStem,
      idEvent: hinted,
      legacyId: legacyPart || null,
      hasLegacySuffix: legacyPart.length > 0,
    };
  }

  // Duplicate files can be named "<id_event>_<legacyId>.json". To avoid false
  // positives for ids naturally ending with "_0", only strip long numeric tails.
  const duplicateMatch = fileStem.match(/^(.*)_(\d{2,})$/);
  if (duplicateMatch && duplicateMatch[1]) {
    return {
      fileStem,
      idEvent: duplicateMatch[1],
      legacyId: duplicateMatch[2],
      hasLegacySuffix: true,
    };
  }

  return {
    fileStem,
    idEvent: fileStem,
    legacyId: null,
    hasLegacySuffix: false,
  };
};

const parseActivitiesTail = (key, hintedIdEvent = "") => {
  if (!key.startsWith(ACTIVITIES_ROOT_PREFIX)) {
    return null;
  }

  const tail = key.slice(ACTIVITIES_ROOT_PREFIX.length).split("/").filter(Boolean);
  if (tail.length === 0) {
    return null;
  }

  let isDated = false;
  let year = null;
  let month = null;
  let day = null;
  let offset = 0;

  const isYear = /^\d{4}$/.test(tail[0] || "");
  const isMonth = /^\d{1,2}$/.test(tail[1] || "");
  const isDay = /^\d{1,2}$/.test(tail[2] || "");
  if (tail.length >= 4 && isYear && isMonth && isDay) {
    isDated = true;
    year = parseInt(tail[0], 10);
    month = parseInt(tail[1], 10);
    day = parseInt(tail[2], 10);
    offset = 3;
  }

  const segment = tail[offset] || "";
  const leaf = tail[tail.length - 1] || "";

  if (segment.toLowerCase().endsWith(".json")) {
    const parsedFile = parseJsonFileName(segment, hintedIdEvent);
    return {
      key,
      isDated,
      year,
      month,
      day,
      isJson: true,
      isImage: false,
      fileName: segment,
      fileStem: parsedFile.fileStem,
      idEvent: parsedFile.idEvent,
      legacyId: parsedFile.legacyId,
      hasLegacySuffix: parsedFile.hasLegacySuffix,
    };
  }

  if (leaf.toLowerCase().endsWith(".json")) {
    const parsedFile = parseJsonFileName(leaf, hintedIdEvent);
    return {
      key,
      isDated,
      year,
      month,
      day,
      isJson: true,
      isImage: false,
      fileName: leaf,
      fileStem: parsedFile.fileStem,
      idEvent: parsedFile.idEvent,
      legacyId: parsedFile.legacyId,
      hasLegacySuffix: parsedFile.hasLegacySuffix,
    };
  }

  if (tail.length - offset >= 2 && segment) {
    return {
      key,
      isDated,
      year,
      month,
      day,
      isJson: false,
      isImage: true,
      fileName: null,
      fileStem: null,
      idEvent: segment,
      legacyId: null,
      hasLegacySuffix: false,
    };
  }

  return null;
};

export const normalizeStorageKey = (input) => {
  const raw = readString(input);
  if (!raw) {
    return "";
  }

  if (raw.startsWith("data:")) {
    return raw;
  }

  const s3Match = raw.match(/^s3:\/\/[^/]+\/(.+)$/i);
  if (s3Match) {
    return stripQueryAndHash(s3Match[1]).replace(/^\/+/, "");
  }

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsedUrl = new URL(raw);
      if (parsedUrl.hostname === SPACES_HOST) {
        return stripQueryAndHash(parsedUrl.pathname).replace(/^\/+/, "");
      }
      return raw;
    } catch (error) {
      return raw;
    }
  }

  return stripQueryAndHash(raw).replace(/^\/+/, "");
};

export const buildSpacesUrlFromKey = (input) => {
  const raw = readString(input);
  if (!raw) {
    return "";
  }

  if (raw.startsWith("data:")) {
    return raw;
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const key = normalizeStorageKey(raw);
  if (!key || /^https?:\/\//i.test(key)) {
    return key;
  }

  return `${SPACES_ENDPOINT}/${key}`;
};

export const parseActivityPath = (input, hintedIdEvent = "") => {
  const normalizedKey = normalizeStorageKey(input);
  if (!normalizedKey || normalizedKey.startsWith("data:") || /^https?:\/\//i.test(normalizedKey)) {
    return null;
  }

  return parseActivitiesTail(normalizedKey, hintedIdEvent);
};

const extractActivityIdFromImages = (activity) => {
  const images = parseImagesInput(activity?.images);
  for (const image of images) {
    const imageUrl = readString(image?.url);
    if (!imageUrl) {
      continue;
    }
    const parsed = parseActivityPath(imageUrl);
    if (parsed?.idEvent) {
      return parsed.idEvent;
    }
  }
  return "";
};

export const extractNormalizedIdEvent = (activity) => {
  const rawId = readActivityRawId(activity);
  const imageId = extractActivityIdFromImages(activity);

  if (imageId) {
    return imageId;
  }

  if (rawId) {
    return rawId;
  }

  const candidates = readActivityPathCandidates(activity);
  for (const candidate of candidates) {
    const parsed = parseActivityPath(candidate, rawId || imageId);
    if (parsed?.idEvent) {
      return parsed.idEvent;
    }
  }

  const fallbackId = readString(activity?.id);
  if (fallbackId) {
    return fallbackId;
  }

  return "";
};

export const normalizeActivityImages = (images) => {
  const readImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (typeof image === "string") {
      return image;
    }

    if (typeof image === "object") {
      return (
        readString(image.url) ||
        readString(image.path) ||
        readString(image.src) ||
        readString(image.image) ||
        ""
      );
    }

    return "";
  };

  return parseImagesInput(images).map((image) => {
    const rawUrl = readImageUrl(image);
    const absoluteUrl = buildSpacesUrlFromKey(rawUrl);

    if (!image || typeof image !== "object") {
      if (!rawUrl) {
        return image;
      }
      return { url: absoluteUrl || rawUrl };
    }

    return {
      ...image,
      url: absoluteUrl || rawUrl || image.url,
    };
  });
};

export const normalizeActivityForUi = (activity) => {
  if (!activity || typeof activity !== "object") {
    return activity;
  }

  const normalizedImages = normalizeActivityImages(activity.images);
  const normalized = {
    ...activity,
    images: normalizedImages,
  };

  const idEvent = extractNormalizedIdEvent(normalized);
  if (idEvent) {
    normalized.id_event = idEvent;
  }

  return normalized;
};

export const normalizeActivitiesForUi = (activities) => {
  if (!Array.isArray(activities)) {
    return [];
  }
  return activities.map((activity) => normalizeActivityForUi(activity));
};

export const getActivityIdentityKey = (activity) => {
  const normalized = normalizeActivityForUi(activity);
  const idEvent = readString(normalized.id_event) || readString(normalized.id) || "unknown_activity";
  const idBusiness =
    readString(normalized?.business?.id_business) ||
    readString(normalized?.id_business) ||
    "unknown_business";
  return `${idEvent}::${idBusiness}`;
};
